// Automation State Service - persystencja stanu automatyzacji
const STORAGE_KEY = 'plexisystem_automation_state';
const API_URL = import.meta.env.PROD 
  ? 'https://api.plexisystem.pl'
  : 'http://localhost:3001';

export interface AutomationState {
  socialMedia: {
    enabled: boolean;
    lastRun?: Date;
    nextRun?: Date;
    schedule: string[];
    platforms: string[];
    stats: {
      postsToday: number;
      postsWeek: number;
      postsMonth: number;
    };
  };
  googleAds: {
    enabled: boolean;
    lastOptimization?: Date;
    nextOptimization?: Date;
    schedule: string;
    features: string[];
    stats: {
      campaignsOptimized: number;
      adsCreated: number;
      budgetSaved: number;
    };
  };
  timeSaved: number; // w godzinach
  lastUpdate: Date;
}

class AutomationStateService {
  private state: AutomationState;
  private saveTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.state = this.loadState();
  }

  // Załaduj stan z localStorage i/lub backendu
  private loadState(): AutomationState {
    // Najpierw spróbuj localStorage
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Konwertuj stringi na daty
        if (parsed.socialMedia?.lastRun) parsed.socialMedia.lastRun = new Date(parsed.socialMedia.lastRun);
        if (parsed.socialMedia?.nextRun) parsed.socialMedia.nextRun = new Date(parsed.socialMedia.nextRun);
        if (parsed.googleAds?.lastOptimization) parsed.googleAds.lastOptimization = new Date(parsed.googleAds.lastOptimization);
        if (parsed.googleAds?.nextOptimization) parsed.googleAds.nextOptimization = new Date(parsed.googleAds.nextOptimization);
        if (parsed.lastUpdate) parsed.lastUpdate = new Date(parsed.lastUpdate);
        
        // Przywróć stan włączenia z osobnych kluczy (dla pewności)
        const socialEnabled = localStorage.getItem('automation_social_enabled');
        const googleEnabled = localStorage.getItem('automation_google_enabled');
        
        if (socialEnabled !== null) {
          parsed.socialMedia.enabled = socialEnabled === 'true';
        }
        if (googleEnabled !== null) {
          parsed.googleAds.enabled = googleEnabled === 'true';
        }
        
        console.log('📦 Loaded automation state from localStorage:', {
          socialEnabled: parsed.socialMedia.enabled,
          googleEnabled: parsed.googleAds.enabled
        });
        
        return parsed;
      } catch (e) {
        console.error('Error parsing automation state:', e);
      }
    }

    // Domyślny stan
    return {
      socialMedia: {
        enabled: false,
        schedule: ['09:00', '13:00', '19:00'],
        platforms: ['Facebook', 'LinkedIn', 'Instagram'],
        stats: {
          postsToday: 0,
          postsWeek: 0,
          postsMonth: 0
        }
      },
      googleAds: {
        enabled: false,
        schedule: '09:00',
        features: ['Nowe reklamy', 'Optymalizacja stawek', 'Analiza konkurencji'],
        stats: {
          campaignsOptimized: 0,
          adsCreated: 0,
          budgetSaved: 0
        }
      },
      timeSaved: 0,
      lastUpdate: new Date()
    };
  }

  // Zapisz stan do localStorage i backendu
  private async saveState() {
    // Zapisz lokalnie
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    
    // Zapisz też osobno flagi włączenia
    localStorage.setItem('automation_social_enabled', String(this.state.socialMedia.enabled));
    localStorage.setItem('automation_google_enabled', String(this.state.googleAds.enabled));

    // Zapisz w backendzie
    try {
      // Pobierz token z AuthService
      const getToken = () => {
        try {
          const session = JSON.parse(localStorage.getItem('plexiSession') || '');
          return session?.session?.token || null;
        } catch {
          return null;
        }
      };
      
      const token = getToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`${API_URL}/api/automation/state`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          socialMediaEnabled: this.state.socialMedia.enabled,
          googleAdsEnabled: this.state.googleAds.enabled,
          state: this.state
        })
      });
    } catch (error) {
      console.error('Error saving automation state to backend:', error);
      // Nie przerywaj działania jeśli backend nie odpowiada
    }
  }

  // Debounced save
  private scheduleSave() {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveState();
    }, 1000);
  }

  // Public API
  getSocialMediaState() {
    return this.state.socialMedia;
  }

  getGoogleAdsState() {
    return this.state.googleAds;
  }

  getStats() {
    return {
      socialMedia: this.state.socialMedia.stats,
      googleAds: this.state.googleAds.stats,
      timeSaved: this.state.timeSaved
    };
  }

  toggleSocialMedia(enabled: boolean) {
    this.state.socialMedia.enabled = enabled;
    if (enabled) {
      this.state.socialMedia.lastRun = new Date();
      this.state.socialMedia.nextRun = this.calculateNextRun(this.state.socialMedia.schedule);
    }
    this.state.lastUpdate = new Date();
    this.scheduleSave();
  }

  toggleGoogleAds(enabled: boolean) {
    this.state.googleAds.enabled = enabled;
    if (enabled) {
      this.state.googleAds.lastOptimization = new Date();
      this.state.googleAds.nextOptimization = this.calculateNextOptimization(this.state.googleAds.schedule);
    }
    this.state.lastUpdate = new Date();
    this.scheduleSave();
  }

  incrementStat(module: 'socialMedia' | 'googleAds', stat: string, value: number = 1) {
    if (module === 'socialMedia' && stat in this.state.socialMedia.stats) {
      (this.state.socialMedia.stats as any)[stat] += value;
    } else if (module === 'googleAds' && stat in this.state.googleAds.stats) {
      (this.state.googleAds.stats as any)[stat] += value;
    }
    this.state.timeSaved += value * 0.25; // Zakładamy 15 min oszczędności na akcję
    this.state.lastUpdate = new Date();
    this.scheduleSave();
  }

  private calculateNextRun(schedule: string[]): Date {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Znajdź następny czas publikacji
    for (const time of schedule.sort()) {
      if (time > currentTime) {
        const [hours, minutes] = time.split(':').map(Number);
        const next = new Date();
        next.setHours(hours, minutes, 0, 0);
        return next;
      }
    }
    
    // Jeśli wszystkie czasy minęły, weź pierwszy jutro
    const [hours, minutes] = schedule[0].split(':').map(Number);
    const next = new Date();
    next.setDate(next.getDate() + 1);
    next.setHours(hours, minutes, 0, 0);
    return next;
  }

  private calculateNextOptimization(schedule: string): Date {
    const [hours, minutes] = schedule.split(':').map(Number);
    const next = new Date();
    next.setDate(next.getDate() + 1); // Jutro
    next.setHours(hours, minutes, 0, 0);
    return next;
  }

  async syncWithBackend() {
    // Pobierz rzeczywiste dane z API
    await this.fetchRealStats();
    
    // Sprawdź stan automatyzacji w backendzie
    try {
      // Pobierz token z AuthService
      const getToken = () => {
        try {
          const session = JSON.parse(localStorage.getItem('plexiSession') || '');
          return session?.session?.token || null;
        } catch {
          return null;
        }
      };
      
      const token = getToken();
      const headers: Record<string, string> = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_URL}/api/automation/state`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        // Synchronizuj stan z backendu jeśli istnieje
        if (data.socialMediaEnabled !== undefined) {
          this.state.socialMedia.enabled = data.socialMediaEnabled;
          localStorage.setItem('automation_social_enabled', String(data.socialMediaEnabled));
        }
        if (data.googleAdsEnabled !== undefined) {
          this.state.googleAds.enabled = data.googleAdsEnabled;
          localStorage.setItem('automation_google_enabled', String(data.googleAdsEnabled));
        }
        
        console.log('🔄 Synced automation state from backend:', {
          socialEnabled: this.state.socialMedia.enabled,
          googleEnabled: this.state.googleAds.enabled
        });
        
        // Zapisz zaktualizowany stan
        this.scheduleSave();
      }
    } catch (error) {
      // Jeśli backend nie odpowiada, użyj lokalnego stanu
      console.log('📦 Using local automation state (backend unavailable)');
    }
    
    /*
    try {
      const response = await fetch(`${API_URL}/api/universal-ai/settings/automation`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Jeśli backend ma dane, użyj ich
        if (data.metadata) {
          try {
            const backendState = JSON.parse(data.metadata);
            this.state = { ...this.state, ...backendState };
            this.saveState();
          } catch (e) {
            console.error('Error parsing backend automation state:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error syncing with backend:', error);
    }
    */
  }

  // Pobierz rzeczywiste statystyki z backendu
  async fetchRealStats() {
    try {
      // Pobierz token z AuthService
      const getToken = () => {
        try {
          const session = JSON.parse(localStorage.getItem('plexiSession') || '');
          return session?.session?.token || null;
        } catch {
          return null;
        }
      };
      
      const token = getToken();
      const headers: Record<string, string> = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Pobierz statystyki Social Media
      const socialResponse = await fetch(`${API_URL}/api/social-media/stats/aggregated?` + new URLSearchParams({
        start_date: new Date(new Date().setHours(0,0,0,0)).toISOString(),
        end_date: new Date().toISOString()
      }), {
        headers
      });
      
      if (socialResponse.ok) {
        const socialStats = await socialResponse.json();
        // Aktualizuj rzeczywiste statystyki
        this.state.socialMedia.stats.postsToday = socialStats.totalPosts || 0;
        
        // Pobierz statystyki tygodniowe
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - 7);
        const weekResponse = await fetch(`${API_URL}/api/social-media/stats/aggregated?` + new URLSearchParams({
          start_date: weekStart.toISOString(),
          end_date: new Date().toISOString()
        }), {
          headers
        });
        
        if (weekResponse.ok) {
          const weekStats = await weekResponse.json();
          this.state.socialMedia.stats.postsWeek = weekStats.totalPosts || 0;
        }
      }
      
      // Pobierz statystyki Google Ads
      const adsResponse = await fetch(`${API_URL}/api/google-ads/campaigns`, {
        headers
      });
      
      if (adsResponse.ok) {
        const campaigns = await adsResponse.json();
        this.state.googleAds.stats.campaignsOptimized = campaigns.filter((c: any) => c.status === 'active').length;
        
        // Pobierz liczbe reklam
        const adsCountResponse = await fetch(`${API_URL}/api/google-ads/ads`, {
          headers
        });
        
        if (adsCountResponse.ok) {
          const ads = await adsCountResponse.json();
          this.state.googleAds.stats.adsCreated = ads.filter((a: any) => a.ai_generated).length;
        }
      }
      
      // Oblicz rzeczywisty zaoszczędzony czas
      const totalActions = this.state.socialMedia.stats.postsToday + 
                          this.state.socialMedia.stats.postsWeek + 
                          this.state.googleAds.stats.campaignsOptimized;
      this.state.timeSaved = totalActions * 0.5; // 30 min per akcję
      
      this.state.lastUpdate = new Date();
      this.scheduleSave();
      
    } catch (error) {
      console.error('Error fetching real stats:', error);
    }
  }
}

// Singleton
export const automationStateService = new AutomationStateService();

// Auto-sync co 5 minut z rzeczywistymi danymi
setInterval(() => {
  automationStateService.syncWithBackend();
  automationStateService.fetchRealStats();
}, 5 * 60 * 1000);

// Initial sync przy starcie
automationStateService.syncWithBackend();
